// B21 — "New matches": every house a Buy Box Agent surfaced in the last 30
// days, tagged with every agent / version / city that found it.
// `?open=<matchId>` (from the email) records the open and jumps to the
// listing in its report, where "Open underwriting" lives. `?day=YYYY-MM-DD`
// (the email's "see all" link) and `?agent=N` filter the list.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Bell, ExternalLink, Calculator, X } from "lucide-react";
import AppLayout from "@/components/app-layout";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import type { BuyBoxMatch } from "@shared/schema";
import { BuyBoxSubnav } from "@/components/buy-box/buy-box-subnav";
import { BuyBoxAgentTags } from "@/components/buy-box/buy-box-agent-tags";

type MatchesResponse = { matches: BuyBoxMatch[]; days: number };
const MATCHES_KEY = ["/api/app/buy-box/matches"];

function usd(cents: number | null): string {
  return cents == null ? "—" : `$${Math.round(cents / 100).toLocaleString()}`;
}
function money(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—";
}

export function readOpenParam(search: string): number | null {
  const raw = new URLSearchParams(search).get("open");
  const n = raw ? Number(raw) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function readMatchFilters(search: string): { day: string | null; agent: number | null } {
  const params = new URLSearchParams(search);
  const day = params.get("day");
  const agentRaw = Number(params.get("agent"));
  return {
    day: day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null,
    agent: Number.isInteger(agentRaw) && agentRaw > 0 ? agentRaw : null,
  };
}

/** The account-local day a match was found (falls back to the browser day for very old rows). */
export function matchDay(m: Pick<BuyBoxMatch, "runDate" | "firstSeenAt">): string {
  if (m.runDate) return m.runDate;
  const d = new Date(m.firstSeenAt);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function filterMatches<T extends Pick<BuyBoxMatch, "runDate" | "firstSeenAt" | "foundBy">>(matches: T[], f: { day: string | null; agent: number | null }): T[] {
  return matches.filter((m) => {
    if (f.day && matchDay(m) !== f.day) return false;
    if (f.agent != null && !(m.foundBy ?? []).some((t) => t.agentNumber === f.agent)) return false;
    return true;
  });
}

export default function AppBuyBoxMatches() {
  usePageTitle("New matches");
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const q = useQuery<MatchesResponse>({ queryKey: MATCHES_KEY });
  const [openParam] = useState(() => (typeof window === "undefined" ? null : readOpenParam(window.location.search)));
  const handledOpen = useRef(false);
  const [filters, setFilters] = useState(() => (typeof window === "undefined" ? { day: null, agent: null } : readMatchFilters(window.location.search)));

  const open = useMutation({
    mutationFn: async (id: number) => (await apiRequest("POST", `/api/app/buy-box/matches/${id}/opened`)).json() as Promise<{ reportId: number | null; resultId: number | null }>,
    onSuccess: (r) => {
      if (r.reportId) navigate(`/app/buy-box/reports/${r.reportId}${r.resultId ? `?result=${r.resultId}` : ""}`);
      else void qc.invalidateQueries({ queryKey: MATCHES_KEY });
    },
    onError: () => toast({ title: "Couldn't open that house", description: "It may have been removed. Try another match.", variant: "destructive" }),
  });
  const dismiss = useMutation({
    mutationFn: async (id: number) => (await apiRequest("POST", `/api/app/buy-box/matches/${id}/dismiss`)).json(),
    meta: { silentSave: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: MATCHES_KEY }),
  });

  useEffect(() => {
    if (openParam && !handledOpen.current) {
      handledOpen.current = true;
      open.mutate(openParam);
    }
  }, [openParam, open]);

  const allMatches = q.data?.matches ?? [];
  const matches = filterMatches(allMatches, filters);
  const days = Array.from(new Set(allMatches.map(matchDay))).sort().reverse();
  if (filters.day && !days.includes(filters.day)) days.unshift(filters.day);
  const agentNumbers = Array.from(new Set(allMatches.flatMap((m) => (m.foundBy ?? []).map((t) => t.agentNumber)).filter((n): n is number => n != null))).sort();
  const updateFilters = (next: { day: string | null; agent: number | null }) => {
    setFilters(next);
    const params = new URLSearchParams();
    if (next.day) params.set("day", next.day);
    if (next.agent != null) params.set("agent", String(next.agent));
    const qs = params.toString();
    window.history.replaceState(null, "", `/app/buy-box/matches${qs ? `?${qs}` : ""}`);
  };
  return (
    <AppLayout>
      <div className="p-6 max-w-4xl">
        <div className="flex items-center justify-between mb-2 gap-3">
          <div className="flex items-center gap-2">
            <Bell className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900" data-tour="buy-box-matches-heading">New matches</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/app/buy-box/agents">
              <span className="text-sm text-blue-600 hover:underline cursor-pointer" data-tour="buy-box-matches-manage">Manage Buy Box Agents</span>
            </Link>
            <ExplainerVideoButton pageKey="buy-box-matches" />
            <GuideButton pageKey="buy-box-matches" />
          </div>
        </div>
        <BuyBoxSubnav />
        <p className="text-sm text-gray-500 mb-4">Newly listed houses your Buy Box Agents found in the last {q.data?.days ?? 30} days. Each house shows up once, tagged with every agent that found it.</p>
        <div className="flex flex-wrap items-center gap-3 mb-6 text-sm" data-testid="filters-buy-box-matches">
          <label className="flex items-center gap-2 text-gray-600">
            Day
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.day ?? ""}
              onChange={(e) => updateFilters({ ...filters, day: e.target.value || null })}
              data-testid="select-matches-day"
            >
              <option value="">All days</option>
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-gray-600">
            Agent
            <select
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              value={filters.agent != null ? String(filters.agent) : ""}
              onChange={(e) => updateFilters({ ...filters, agent: e.target.value ? Number(e.target.value) : null })}
              data-testid="select-matches-agent"
            >
              <option value="">All agents</option>
              {agentNumbers.map((n) => <option key={n} value={String(n)}>Agent {n}</option>)}
            </select>
          </label>
          {(filters.day || filters.agent != null) && (
            <span className="text-gray-500" data-testid="text-matches-filtered-count">
              {matches.length} of {allMatches.length} matches
            </span>
          )}
        </div>

        {q.isLoading || (openParam && open.isPending) ? (
          <Skeleton className="h-40 w-full" />
        ) : q.isError ? (
          <p className="text-sm text-red-600">Couldn't load your matches. Refresh to try again.</p>
        ) : matches.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500" data-testid="text-no-matches">
            {allMatches.length > 0 ? "No matches for these filters." : "No new matches yet. Your agents run every morning around 6am."}
          </div>
        ) : (
          <ul className="space-y-3" data-testid="list-buy-box-matches">
            {matches.map((m) => (
              <li key={m.id} className="bg-white border border-gray-200 rounded-xl p-4 flex gap-4 items-center" data-testid={`row-match-${m.id}`}>
                {m.imageUrl ? <img src={m.imageUrl} alt="" className="h-16 w-24 rounded-md object-cover shrink-0" /> : null}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate">{m.address || "Address not listed"}</div>
                  <div className="text-xs text-gray-500">
                    {[m.city, m.state].filter(Boolean).join(", ")} · {usd(m.priceCents)}
                    {m.beds != null ? ` · ${m.beds} bd` : ""}
                    {m.baths != null ? ` · ${Number(m.baths)} ba` : ""}
                    {m.monthlyCashFlow != null ? ` · ${money(m.monthlyCashFlow)}/mo cash flow` : ""}
                  </div>
                  <BuyBoxAgentTags tags={m.foundBy} testId={`tags-match-${m.id}`} />
                  <div className="text-xs text-gray-400">
                    Found {matchDay(m)}
                    {m.openedAt ? " · Opened" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {m.listingUrl && (
                    <a href={m.listingUrl} target="_blank" rel="noreferrer" className="p-2 text-gray-500 hover:text-gray-800" aria-label="Open listing">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <Button size="sm" onClick={() => open.mutate(m.id)} disabled={open.isPending} data-testid={`button-open-match-${m.id}`}>
                    <Calculator className="h-4 w-4 mr-1" /> Underwrite
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => dismiss.mutate(m.id)} aria-label="Hide this match" data-testid={`button-dismiss-match-${m.id}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  );
}
